import React, { useMemo } from 'react';
import { Card } from '../../CompositeComponents/Card/Card';
import type { BarChartProps } from './types';

// ─── BarChart ─────────────────────────────────────────────────────────────────

const W = 400;
const H = 180;
const PAD = { top: 12, right: 16, bottom: 40, left: 48 };
const DEFAULT_COLOR = '#6366f1';

const DEFAULT_FORMATTER = (v: number): string =>
  v.toLocaleString('en-IN', { maximumFractionDigits: 0 });

export function BarChart({
  data,
  title,
  xAxisLabel,
  yAxisLabel,
  valueFormatter = DEFAULT_FORMATTER,
  className,
}: BarChartProps) {
  const maxVal = useMemo(() => Math.max(...data.map((d) => d.value), 1), [data]);

  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const barGap = 4;
  const barW = data.length > 0 ? Math.max(4, innerW / data.length - barGap) : 0;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((pct) => ({
    y: PAD.top + innerH - pct * innerH,
    label: valueFormatter(maxVal * pct),
  }));

  return (
    <Card className={className}>
      {title && (
        <p className="mb-3 text-sm font-semibold text-neutral-700 dark:text-neutral-200">{title}</p>
      )}

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        role="img"
        aria-label={title ?? 'Bar chart'}
      >
        {/* Grid lines */}
        {yTicks.map((t) => (
          <line
            key={t.y}
            x1={PAD.left}
            y1={t.y}
            x2={W - PAD.right}
            y2={t.y}
            stroke="#e5e7eb"
            strokeWidth="1"
          />
        ))}

        {/* Y-axis labels */}
        {yTicks.map((t) => (
          <text key={t.y} x={PAD.left - 4} y={t.y + 4} textAnchor="end" fontSize="9" fill="#9ca3af">
            {t.label}
          </text>
        ))}

        {/* Y-axis title */}
        {yAxisLabel && (
          <text
            x={10}
            y={PAD.top + innerH / 2}
            textAnchor="middle"
            fontSize="9"
            fill="#9ca3af"
            transform={`rotate(-90, 10, ${PAD.top + innerH / 2})`}
          >
            {yAxisLabel}
          </text>
        )}

        {/* Bars + X labels */}
        {data.map((d, i) => {
          const slotW = innerW / data.length;
          const x = PAD.left + i * slotW + (slotW - barW) / 2;
          const barH = maxVal > 0 ? (d.value / maxVal) * innerH : 0;
          const y = PAD.top + innerH - barH;
          const color = d.color ?? DEFAULT_COLOR;

          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={barH} fill={color} rx="2">
                <title>{`${d.label}: ${valueFormatter(d.value)}`}</title>
              </rect>
              <text
                x={x + barW / 2}
                y={H - PAD.bottom + 12}
                textAnchor="middle"
                fontSize="9"
                fill="#9ca3af"
              >
                {d.label.length > 6 ? d.label.slice(0, 5) + '…' : d.label}
              </text>
            </g>
          );
        })}

        {/* X-axis label */}
        {xAxisLabel && (
          <text
            x={PAD.left + innerW / 2}
            y={H - 2}
            textAnchor="middle"
            fontSize="9"
            fill="#9ca3af"
          >
            {xAxisLabel}
          </text>
        )}
      </svg>
    </Card>
  );
}
